import React, { useEffect, useState } from "react";
import "./BusSeatLayout.css";
import { TbSteeringWheel } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import moment from "moment";
import axios from "axios";
import { ClipLoader } from "react-spinners";

const BusSeatLayout = ({
  seatData,
  boardingDropping,
  boardingLoader,
  busInfo,
  DateOfJourney,
}) => {
  const seatDetailsRaw = seatData?.SeatLayout?.SeatDetails || [];
  const busDetails = busInfo || seatData?.busInfo || {};

  // --- Separate upper and lower deck ---
  const isUpperSeat = (seat) => {
    if (!seat) return false;
    if (seat.IsUpper !== undefined) return seat.IsUpper;
    const type = (seat.SeatType || "").toLowerCase();
    return /upper|upp/.test(type);
  };

  // flatten first
  const allSeats = seatDetailsRaw.flat(); // merge nested rows into one array
  const hasUpperSeats = allSeats.some((seat) => isUpperSeat(seat));
  const hasLowerSeats = allSeats.some((seat) => !isUpperSeat(seat));

  const [selectedSeats, setSelectedSeats] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [selectedBoarding, setSelectedBoarding] = useState(null);
  const [selectedDropping, setSelectedDropping] = useState(null);

  const boardingPoints = boardingDropping?.boardingPoints || [];
  const droppingPoints = boardingDropping?.droppingPoints || [];

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedSeats.length > 0) {
      setActiveTab("boarding");
    } else {
      setActiveTab(null);
      setSelectedBoarding(null);
      setSelectedDropping(null);
    }
  }, [selectedSeats]);

  const getSeatId = (s) =>
    s?.SeatIndex ?? s?.SeatId ?? s?.SeatName ?? `${Math.random()}`;

  const toggleSeat = (seat) => {
    const id = getSeatId(seat);
    setSelectedSeats((prev) => {
      const already = prev.find((s) => getSeatId(s) === id);
      if (already) return prev.filter((s) => getSeatId(s) !== id);
      return [...prev, seat];
    });
  };

  // --- Helper functions to safely get row/column ---
  const getRow = (seat) => {
    return parseInt(seat.RowNo ?? seat.Row ?? seat.SeatRow ?? 0, 10);
  };

  const getCol = (seat) => {
    return parseInt(seat.ColumnNo ?? seat.Column ?? seat.SeatColumn ?? 0, 10);
  };

  // --- New: render by coordinates ---
  const renderSeatsGrid = (seats, deck) => {
    if (!seats.length) return null;

    const maxRow = Math.max(...seats.map((s) => getRow(s)));
    const maxCol = Math.max(...seats.map((s) => getCol(s)));

    return (
      <div
        className={`busSeatLayout-seats ${deck}`}
        style={{
          display: "grid",
          gridTemplateRows: `repeat(${maxCol + 1}, 40px)`,
          gridTemplateColumns: `repeat(${maxRow + 1}, 40px)`,
          gap: "6px",
        }}
      >
        {seats.map((seat) =>
          renderSeat(seat, getSeatId(seat), deck, {
            gridRowStart: getCol(seat) + 1,
            // Important: column ko reverse kar rahe
            gridColumnStart: maxRow - getRow(seat) + 1,
          })
        )}
      </div>
    );
  };

  // --- Seat renderer ---
  const renderSeat = (seat, key, deck, style = {}) => {
    if (!seat) return <div key={key} className="seat empty" />;

    const isBooked =
      seat.SeatStatus === "BOOKED" ||
      seat.SeatStatus === false ||
      seat.IsBlocked ||
      seat.IsBooked ||
      seat.IsAvailable === false;

    const isSelected = selectedSeats.some(
      (s) => getSeatId(s) === getSeatId(seat)
    );

    const isLadies =
      seat.IsLadiesSeat ||
      String(seat.SeatClass || "")
        .toLowerCase()
        .includes("ladies");

    const seatClass = [
      "seat",
      isBooked ? "booked" : "available",
      isLadies ? "ladies" : "",
      isSelected ? "selected" : "",
      seat.SeatType === 1
        ? "seater"
        : seat.SeatType === 2
        ? "sleeper"
        : deck === "upper"
        ? "sleeper"
        : "seater",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        key={key}
        className={seatClass}
        style={style}
        onClick={() => !isBooked && toggleSeat(seat)}
      >
        <div className="seat-name">{seat.SeatName}</div>
        <div className="seat-fare">
          ₹{seat.Price?.PublishedPriceRoundedOff || 0}
        </div>
      </div>
    );
  };

  const navigate = useNavigate();

  const handleContinue = async () => {
    if (
      selectedBoarding === null ||
      selectedDropping === null ||
      selectedSeats.length === 0
    ) {
      alert("Please select seat, boarding and dropping point.");
      return;
    }

    try {
      setLoading(true); // start loader
      const tokenId = Date.now(); // unique token
      const boarding = boardingPoints[selectedBoarding];
      const dropping = droppingPoints[selectedDropping];

      const seatPayload = selectedSeats.map((s) => ({
        ...s,
      }));

      const totalFare = seatPayload.reduce(
        (acc, seat) =>
          acc + (seat.SeatFare || seat.Price?.PublishedPriceRoundedOff || 0),
        0
      );

      const storePayload = {
        TokenId: tokenId,
        TraceId: busDetails.TraceId,
        ResultIndex: busDetails.ResultIndex,
        Destination: busDetails.Destination || "",
        Origin: busDetails.Origin || "",
        DepartureTime: busDetails.DepartureTime || "",
        ArrivalTime: busDetails.ArrivalTime || "",
        NoofPassengers: seatPayload.length,
        BoardingTimeAddress: boarding?.CityPointTime || "",
        BoardingAddress: boarding?.CityPointName || "",
        BoardingPointId: boarding?.CityPointIndex || "",
        DroppingTime: dropping?.CityPointTime || "",
        DroppingAddress: dropping?.CityPointName || "",
        DroppingPointId: dropping?.CityPointIndex || "",
        TravelName: busDetails.TravelName || "",
        BusType: busDetails.BusType || "",
        Seats: seatPayload,
        TotalFare: totalFare,
      };

      await axios.post(
        "https://admin.travelsdata.com/api/Bus/StoreData",
        storePayload
      );

      // Navigate with state only (no localStorage)
      navigate(`/busprebook?tokenId=${tokenId}`, {
        state: {
          storePayload, // full payload
        },
      });
    } catch (error) {
      console.error("Error storing bus data:", error);
      alert("Failed to continue. Please try again.");
    } finally {
      setLoading(false); // stop loader
    }
  };

  // calculate total fare using PublishedPriceRoundedOff
  const totalFare = selectedSeats.reduce(
    (acc, seat) => acc + (seat.Price?.PublishedPriceRoundedOff || 0),
    0
  );

  console.log("SeatLayout:", seatData);

  return (
    <div className="busSeatLayout">
      <div className="busSeatLayout-left">
        <div className="busSeatLayout-stepTitle">
          <span className="step">1</span> Select Your Seat
        </div>

        <div className="busSeatLayout-legend">
          <div>
            <span className="legend ladies" /> Ladies
          </div>
          <div>
            <span className="legend selected" /> Selected
          </div>
          <div>
            <span className="legend available" /> Available
          </div>
          <div>
            <span className="legend booked" /> Booked
          </div>
        </div>

        <div className="busSeatLayout-bus">
          <div className="busSeatLayout-seatContainer">
            {hasLowerSeats && (
              <div className="seat-section">
                <div className="seat-section-title">
                  <TbSteeringWheel className="busSteeringWheel" /> LOWER DECK
                </div>
                {renderSeatsGrid(
                  allSeats.filter((s) => !isUpperSeat(s)),
                  "lower"
                )}
              </div>
            )}

            {hasUpperSeats && (
              <div className="seat-section">
                <div className="seat-section-title">UPPER DECK</div>
                {renderSeatsGrid(
                  allSeats.filter((s) => isUpperSeat(s)),
                  "upper"
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedSeats.length > 0 && (
        <div className="busSeatLayout-right">
          <div className="busSeatLayout-stepTitle">
            <span className="step">2</span> Select Boarding And Dropping Point
          </div>

          {boardingLoader ? (
            <div className="Buslisting-seatLoader">
              <PropagateLoader color="#1a4f87" />
            </div>
          ) : selectedBoarding !== null && selectedDropping !== null ? (
            // Summary after both points selected
            <div className="busSeatLayout-summary">
              <div className="busSeatLayout-summaryItem">
                <div className="boardingDetails">
                  <strong>Boarding Point:</strong>
                  <div>{boardingPoints[selectedBoarding]?.CityPointName}</div>
                  <div>
                    {boardingPoints[selectedBoarding]?.CityPointAddress}
                  </div>
                  <div>
                    {new Date(
                      boardingPoints[selectedBoarding]?.CityPointTime
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <button
                  className="changeBtn"
                  onClick={() => {
                    setSelectedBoarding(null);
                    setActiveTab("boarding");
                  }}
                >
                  Change
                </button>
              </div>

              <div className="busSeatLayout-summaryItem">
                <div className="boardingDetails">
                  <strong>Dropping Point:</strong>
                  <div>{droppingPoints[selectedDropping]?.CityPointName}</div>
                  <div>
                    {droppingPoints[selectedDropping]?.CityPointLocation}
                  </div>
                  <div>
                    {new Date(
                      droppingPoints[selectedDropping]?.CityPointTime
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <button
                  className="changeBtn"
                  onClick={() => {
                    setSelectedDropping(null);
                    setActiveTab("dropping");
                  }}
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            // Boarding / Dropping selection tabs
            <>
              <div className="busSeatLayout-tabs">
                <button
                  className={activeTab === "boarding" ? "active" : ""}
                  onClick={() => setActiveTab("boarding")}
                >
                  Boarding Point
                </button>
                <button
                  className={activeTab === "dropping" ? "active" : ""}
                  onClick={() => setActiveTab("dropping")}
                  disabled={selectedBoarding === null}
                >
                  Dropping Point
                </button>
              </div>

              <div className="busSeatLayout-points">
                {activeTab === "boarding" &&
                  boardingPoints.map((point, idx) => (
                    <label className="busSeatLayout-pointOption" key={idx}>
                      <input
                        type="radio"
                        name="boarding"
                        checked={selectedBoarding === idx}
                        onChange={() => {
                          setSelectedBoarding(idx);
                          setActiveTab("dropping");
                        }}
                      />
                      <div>
                        <strong>
                          {new Date(point.CityPointTime).toLocaleTimeString(
                            [],
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </strong>
                        <div>{point.CityPointName}</div>
                        <div>
                          {point.CityPointAddress ||
                            point.CityPointLandmark ||
                            point.CityPointLocation}
                        </div>
                      </div>
                    </label>
                  ))}

                {activeTab === "dropping" &&
                  droppingPoints.map((point, idx) => (
                    <label className="busSeatLayout-pointOption" key={idx}>
                      <input
                        type="radio"
                        name="dropping"
                        checked={selectedDropping === idx}
                        onChange={() => setSelectedDropping(idx)}
                      />
                      <div>
                        <strong>
                          {new Date(point.CityPointTime).toLocaleTimeString(
                            [],
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </strong>
                        <div>{point.CityPointName}</div>
                        <div>
                          {point.CityPointAddress ||
                            point.CityPointLandmark ||
                            point.CityPointLocation}
                        </div>
                      </div>
                    </label>
                  ))}
              </div>
            </>
          )}

          <button
            className="continueBtn"
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? <ClipLoader size={25} color="#fff" /> : "Continue"}
          </button>

          <div className="busSeatLayout-grandTotal">
            <div className="busSeatLayout-grandTotalSeatSelected">
              <span>Seat Selected</span>
              <div className="busgrandTotalSeatSelected">
                {selectedSeats.map((seat, idx) => (
                  <span key={idx}>{seat.SeatName} </span>
                ))}
              </div>
            </div>
            <div className="busSeatLayout-busgrandTotal">
              Grand Total
              <div className="busSeatLayout-grandTotalPrice">₹{totalFare}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusSeatLayout;
